import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { holdExpiresAtIso, holdExpiryHours, isThisWeek } from '@/domain/depositWindow';
import { onFileExpiryIso } from '@/domain/confirmationFlow';
import { recallOverlappingHolds } from '@/domain/resendRoutingFlow';
import { depositPaidAud } from '@/utils/bookingSummary';
import { resolveNoDepositDecline } from '@/domain/noDepositDeclineFlow';
import { seedNow, seedId } from '@/domain/seedContext';
import type {
  Booking,
  Deposit,
  DepositChannel,
  DepositLedgerEntry,
  SelfBookLink,
  StudioId,
} from '@/domain/types';

const STRIPE_FEE_RATE = 0.0175;
const STRIPE_FIXED_FEE = 0.3;

/** Reserved identity for a slot held by a link before a client has claimed it. */
export const AWAITING_CLIENT = 'awaiting-client';

export interface DepositLinkInput {
  customerId: string;
  studioId: StudioId;
  artistId: string;
  amountAud: number;
  channel: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
  sessionLength: Booking['sessionLength'];
  source: Booking['source'];
  bookingStartIso?: string;
  designBrief?: string;
  referencePhotos?: string[];
  /**
   * Slot discussed + pinned on the call. When present, `discussedSlot.artistId`
   * becomes the deposit's artist (instead of `input.artistId`) so the later
   * booking lands with the right artist.
   */
  discussedSlot?: { artistId: string; startIso: string; endIso: string };
  /** Human-readable piece name captured on the call. */
  pieceName?: string;
  /** Reconciliation code from the self-book link this deposit was taken through. */
  depositReference?: string;
  /**
   * Structured brief captured on the previous screen. When a slot is grounded,
   * these ride onto the held ghost booking so it opens pre-filled in the booking
   * editor (add reference images etc.) rather than as a bare hold.
   */
  placement?: string;
  bodyPart?: Booking['bodyPart'];
  bodyPartSide?: Booking['bodyPartSide'];
  bodyPartQualifier?: Booking['bodyPartQualifier'];
  styleTags?: string[];
  colour?: 'colour' | 'black-and-grey';
  collabArtistIds?: string[];
  /** Existing piece this session continues (from the brief's Continue mode). */
  pieceId?: string;
  /** Session day rate quoted at send time (built from the artist rate x window). */
  dayRateAud?: number;
  /**
   * Firm hold: opt out of the this-week +2h grace and the overbookable flag, so
   * the hold is a flat 24h lock with no overbook (tv-nid). Set by the Sweeper
   * gap-fill dated-link path; agent dated-links leave it unset (overbookable when
   * this week).
   */
  firmHold?: boolean;
}

function nowIso() {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/** The current session operator, for per-agent deposit attribution (Prompt F). */
function currentAgentId(): string | undefined {
  return useSessionStore.getState().agentId ?? undefined;
}

export function generateDepositLink(input: DepositLinkInput): Deposit {
  const id = seedId('dep') ?? `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const now = nowIso();
  const slot = input.discussedSlot;
  const expiresAt = slot
    ? holdExpiresAtIso(slot.startIso, now, { firm: input.firmHold })
    : new Date(new Date(now).getTime() + 48 * 60 * 60 * 1000).toISOString();
  const heldBookingId = slot ? `bk-${id}` : undefined;
  // A this-week agent dated-link holds an OVERBOOKABLE ghost: the near slot may be
  // double-held and locks to whoever pays first (tv-nid). Firm (gap-fill) and
  // out-of-week holds are never overbookable.
  const overbookable = !!slot && !input.firmHold && isThisWeek(slot.startIso, now);
  const deposit: Deposit = {
    id,
    customerId: input.customerId,
    studioId: input.studioId,
    artistId: slot?.artistId ?? input.artistId,
    amountAud: input.amountAud,
    feesAud: Number((input.amountAud * STRIPE_FEE_RATE + STRIPE_FIXED_FEE).toFixed(2)),
    netAud: Number(
      (input.amountAud - input.amountAud * STRIPE_FEE_RATE - STRIPE_FIXED_FEE).toFixed(2),
    ),
    status: 'pending',
    channelSent: input.channel,
    stripePaymentLinkId: `plink_mock_${id}`,
    expiresAt,
    createdAt: now,
    policy: 'seven-day-rule',
    source: input.source,
    ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
    ...(slot ? { discussedSlot: slot } : {}),
    ...(heldBookingId ? { heldBookingId } : {}),
    ...(input.pieceName ? { pieceName: input.pieceName } : {}),
    ...(input.depositReference ? { depositReference: input.depositReference } : {}),
  };
  const brain = useBrainStore.getState();
  brain.upsertDeposit(deposit);

  if (slot && heldBookingId) {
    const durationMin = Math.max(
      15,
      Math.round((new Date(slot.endIso).getTime() - new Date(slot.startIso).getTime()) / 60000),
    );
    const held: Booking = {
      id: heldBookingId,
      customerId: input.customerId,
      studioId: input.studioId,
      artistId: slot.artistId,
      start: slot.startIso,
      durationMin,
      sessionLength: input.sessionLength,
      status: 'held',
      depositId: id,
      ...(overbookable ? { overbookable: true } : {}),
      designBrief: input.pieceName ?? 'Held on deposit link.',
      referencePhotos: input.referencePhotos ?? [],
      source: input.source as Booking['source'],
      createdAt: now,
      // Carry the structured brief so the ghost booking is editable in place
      // (the booking editor reads these), not a bare hold.
      ...(input.placement ? { placement: input.placement } : {}),
      ...(input.bodyPart ? { bodyPart: input.bodyPart } : {}),
      ...(input.bodyPartSide ? { bodyPartSide: input.bodyPartSide } : {}),
      ...(input.bodyPartQualifier ? { bodyPartQualifier: input.bodyPartQualifier } : {}),
      ...(input.styleTags && input.styleTags.length ? { styleTags: input.styleTags } : {}),
      ...(input.colour ? { colour: input.colour } : {}),
      ...(input.collabArtistIds && input.collabArtistIds.length
        ? { collabArtistIds: input.collabArtistIds }
        : {}),
      ...(input.pieceId ? { pieceId: input.pieceId } : {}),
      ...(input.dayRateAud != null ? { dayRateAud: input.dayRateAud } : {}),
    };
    brain.upsertBooking(held);
    brain.appendActivity({
      id: `act-${id}-hold`,
      type: 'booking-created',
      customerId: input.customerId,
      studioId: input.studioId,
      artistId: slot.artistId,
      body: `Held ${slot.artistId}'s slot ${new Date(slot.startIso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} pending deposit.`,
      timestamp: now,
    });
  }

  const hours = slot ? holdExpiryHours(slot.startIso, now) : 48;
  brain.appendActivity({
    id: `act-${id}`,
    type: 'deposit-link-sent',
    customerId: input.customerId,
    studioId: input.studioId,
    artistId: input.artistId,
    amount: input.amountAud,
    body: input.customerId === AWAITING_CLIENT
      ? `Self-book slot held via artist link. ${formatAud(input.amountAud)}, expires in ${hours}h.`
      : `Stripe deposit link sent via ${input.channel} to ${input.customerId}. ${formatAud(input.amountAud)}, expires in ${hours}h.`,
    timestamp: now,
  });
  return deposit;
}

function formatAud(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  }).format(n);
}

/**
 * A successful deposit payment issues a receipt to the customer (Diagram 02:
 * "the system collects the phone number and sends the receipt") and confirms the
 * phone is a real SMS channel on file. Prototype-only: no real SMS, just the
 * activity record + the number-on-file stamp. Module-private, so it adds no new
 * export (console coverage unaffected). Fires once per payment instant.
 */
function emitDepositReceipt(deposit: Deposit, amountAud: number, at: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[deposit.customerId];
  if (customer && !customer.numberOnFile) {
    brain.upsertCustomer({ ...customer, numberOnFile: true });
  }
  brain.appendActivity({
    id: `act-receipt-${deposit.id}-${at}`,
    type: 'receipt-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId,
    amount: amountAud,
    body: `Receipt for ${formatAud(amountAud)} deposit sent to ${deposit.customerId}. Number on file.`,
    timestamp: at,
  });
}

export function simulateDepositLinkViewed(depositId: string) {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[depositId];
  if (!deposit) return;
  if (deposit.status !== 'pending' || deposit.viewedAt) return;
  brain.upsertDeposit({ ...deposit, viewedAt: nowIso() });
  brain.appendActivity({
    id: `act-dep-view-${deposit.id}`,
    type: 'deposit-link-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    body: `${deposit.customerId} opened the deposit link.`,
    timestamp: nowIso(),
  });
}

export function simulateStripeWebhookPaid(opts: {
  depositId: string;
  bookingStartIso?: string;
  designBrief?: string;
  referencePhotos?: string[];
  /** Structured brief fields captured at booking time, merged onto the created booking. */
  bookingOverrides?: Partial<Booking>;
}) {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return;
  // Idempotency / replay guard: only a still-pending deposit is actionable. A late
  // or replayed webhook on one already paid/expired/cancelled must be a no-op - an
  // expired hold's slot has been released, so re-confirming it would resurrect a
  // cancelled booking, double-count revenue, and text the client a false "locked
  // in" (reconcileDepositHolds leaves heldBookingId in place, so the branch below
  // would otherwise re-enter).
  if (deposit.status !== 'pending') return;

  // tv-b26: an actual deposit landing clears any no-deposit decline flag on this
  // customer (covers all three confirm branches below with one call).
  resolveNoDepositDecline(deposit.customerId);

  // Held-placeholder path: the slot was already held on send. Confirm THAT booking
  // in place (no fresh booking), promote heldBookingId -> bookingId, and lock it.
  if (deposit.heldBookingId) {
    const held = brain.bookings[deposit.heldBookingId];
    if (held && held.status === 'held') {
      const confirmed: Booking = {
        ...held,
        status: 'confirmed',
        confirmedAt: nowIso(),
        ...opts.bookingOverrides,
      };
      brain.upsertBooking(confirmed);
      const { heldBookingId: _promoted, ...rest } = deposit;
      brain.upsertDeposit({
        ...rest,
        status: 'paid',
        amountPaid: deposit.amountAud,
        paidAt: nowIso(),
        bookingId: confirmed.id,
        ledger: [
          ...(deposit.ledger ?? []),
          { at: nowIso(), amountAud: deposit.amountAud, kind: 'paid', by: 'stripe', note: 'Stripe link' },
        ],
      });
      brain.incStat('deposits');
      brain.incStat('networkRevenueAud', deposit.amountAud);
      brain.appendActivity({
        id: `act-${confirmed.id}-confirmed`,
        type: 'deposit-paid',
        customerId: deposit.customerId,
        studioId: deposit.studioId,
        artistId: confirmed.artistId,
        amount: deposit.amountAud,
        body: `Deposit ${formatAud(deposit.amountAud)} paid by ${deposit.customerId}. Held slot confirmed.`,
        timestamp: nowIso(),
      });
      brain.clearSequenceState(deposit.customerId);
      emitDepositReceipt(deposit, deposit.amountAud, nowIso());
      playSound('deposit-chime');
      eventBus.publish('stripe-webhook-success', { bookingId: confirmed.id, depositId: deposit.id });
      onBookingConfirmed(confirmed.id);
      // Someone paid first: void + re-call any other overbookable hold on this slot.
      recallOverlappingHolds(confirmed.id);
      return;
    }
  }

  // Effective slot: an explicit bookingStartIso wins, else fall back to the slot
  // grounded onto the deposit when the link was sent (the gap-fill / discussed-slot
  // path). This makes a grounded deposit land in its slot the moment it's paid.
  const groundedStartIso = opts.bookingStartIso ?? deposit.discussedSlot?.startIso;

  // No slot at all (canonical cold path: agent sends deposit, customer pays,
  // THEN the queue surfaces a placement task and the agent picks the slot).
  // Mark the deposit paid, fire the activity, leave the booking for the agent
  // to place. The deposit-paid event is what drives the queue's deposit-on-file
  // item via queueEngine.
  if (!groundedStartIso) {
    const paidAt = nowIso();
    brain.upsertDeposit({
      ...deposit,
      status: 'paid',
      amountPaid: deposit.amountAud,
      paidAt,
      // Dateless on file (the "abyss", Diagram 02): valid 12 months from the
      // instant it went on file, so the Sweeper chases it in the on-file cohort.
      expiresAt: onFileExpiryIso(paidAt),
      ledger: [...(deposit.ledger ?? []), { at: nowIso(), amountAud: deposit.amountAud, kind: 'paid', by: 'stripe', note: 'Stripe link' }],
    });
    brain.incStat('deposits');
    brain.incStat('networkRevenueAud', deposit.amountAud);
    brain.appendActivity({
      id: `act-dep-paid-${deposit.id}`,
      type: 'deposit-paid',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: deposit.artistId,
      amount: deposit.amountAud,
      body: `Deposit ${formatAud(deposit.amountAud)} paid by ${deposit.customerId}. Place their booking now.`,
      timestamp: nowIso(),
    });
    const target = autoPingChannelFor('deposit-paid', deposit.studioId, brain.internalComms);
    if (target) {
      brain.addAutoPing(
        target,
        `💰 ${formatAud(deposit.amountAud)} deposit landed · ${deposit.studioId.replace('-', ' ')} · awaiting placement.`,
        { customerRefId: deposit.customerId, timestamp: nowIso() },
      );
    }
    emitDepositReceipt(deposit, deposit.amountAud, paidAt);
    playSound('deposit-chime');
    eventBus.publish('stripe-webhook-success', { depositId: deposit.id });
    return;
  }

  // Slot already known: canonical scripted scenarios + self-book flow pre-pick
  // the time so we can confirm the booking the moment the webhook fires.
  const bookingId = `bk-${deposit.id}`;
  const startIso = groundedStartIso;
  const sessionLength: Booking['sessionLength'] =
    deposit.amountAud >= 500 ? 'full-day' : deposit.amountAud >= 300 ? 'half-day' : 'small';
  const durationMin = sessionLength === 'full-day' ? 480 : sessionLength === 'half-day' ? 240 : 120;

  const booking: Booking = {
    id: bookingId,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId ?? 'pat',
    start: startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    depositId: deposit.id,
    designBrief: opts.designBrief ?? 'Confirmed booking via simulated Stripe webhook.',
    referencePhotos: opts.referencePhotos ?? [],
    source: deposit.source as Booking['source'],
    createdAt: nowIso(),
    ...opts.bookingOverrides,
  };

  brain.upsertDeposit({
    ...deposit,
    status: 'paid',
    amountPaid: deposit.amountAud,
    paidAt: nowIso(),
    bookingId,
    ledger: [...(deposit.ledger ?? []), { at: nowIso(), amountAud: deposit.amountAud, kind: 'paid', by: 'stripe', note: 'Stripe link' }],
  });
  brain.upsertBooking(booking);
  brain.incStat('deposits');
  brain.incStat('networkRevenueAud', deposit.amountAud);
  brain.appendActivity({
    id: `act-${bookingId}`,
    type: 'deposit-paid',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: booking.artistId,
    amount: deposit.amountAud,
    body: `Deposit ${formatAud(deposit.amountAud)} paid by ${deposit.customerId}. Booking confirmed.`,
    timestamp: nowIso(),
  });
  brain.appendActivity({
    id: `act-${bookingId}-booking`,
    type: 'booking-created',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: booking.artistId,
    body: `${deposit.customerId} x ${booking.artistId}. ${new Date(startIso).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. ${sessionLength}.`,
    timestamp: nowIso(),
  });
  emitDepositReceipt(deposit, deposit.amountAud, nowIso());
  playSound('deposit-chime');
  eventBus.publish('stripe-webhook-success', { bookingId, depositId: deposit.id });
  // Booking just reached `confirmed` - auto-send the pre-session waiver.
  onBookingConfirmed(bookingId);
  // Someone paid first: void + re-call any other overbookable hold on this slot.
  recallOverlappingHolds(bookingId);
}

export function placeBookingFromDeposit(opts: {
  depositId: string;
  artistId: string;
  startIso: string;
  durationMin?: number;
  designBrief?: string;
}) {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return;
  if (deposit.status !== 'paid') return;
  if (deposit.bookingId) return; // already placed

  const sessionLength: Booking['sessionLength'] =
    deposit.amountAud >= 500 ? 'full-day' : deposit.amountAud >= 300 ? 'half-day' : 'small';
  const durationMin =
    opts.durationMin ??
    (sessionLength === 'full-day' ? 480 : sessionLength === 'half-day' ? 240 : 120);

  const bookingId = `bk-${deposit.id}`;
  const booking: Booking = {
    id: bookingId,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: opts.artistId,
    start: opts.startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    depositId: deposit.id,
    designBrief:
      opts.designBrief ?? deposit.pieceName ?? 'Booking placed after deposit paid.',
    referencePhotos: [],
    source: deposit.source as Booking['source'],
    createdAt: nowIso(),
    ...(currentAgentId() ? { bookedByAgentId: currentAgentId() } : {}),
  };

  brain.upsertDeposit({ ...deposit, bookingId, artistId: opts.artistId });
  brain.upsertBooking(booking);
  brain.appendActivity({
    id: `act-${bookingId}-placed`,
    type: 'booking-created',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: opts.artistId,
    body: `Placed ${deposit.customerId} with ${opts.artistId} on ${new Date(opts.startIso).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.`,
    timestamp: nowIso(),
  });
  brain.clearSequenceState(deposit.customerId);
  eventBus.publish('booking:placed', { bookingId, depositId: deposit.id });
  // Booking just reached `confirmed` - auto-send the pre-session waiver.
  onBookingConfirmed(bookingId);
  // Slot now locked: void + re-call any other overbookable hold on this slot.
  recallOverlappingHolds(bookingId);
}

const MANUAL_METHOD_LABEL: Record<'eftpos' | 'cash' | 'bank-transfer', string> = {
  eftpos: 'EFTPOS',
  cash: 'Cash',
  'bank-transfer': 'Bank transfer',
};

/**
 * Record a deposit payment taken by a non-Stripe method (EFTPOS / cash / bank
 * transfer). Appends a ledger entry and bumps the deposit's paid amount - the
 * money row's "deposit paid" and balance derive from this, so nothing is
 * hand-typed. Every successful payment issues a receipt (Diagram 02).
 *
 * Two shapes:
 * - `bookingId`: pay against a dated booking; creates the deposit if it has none.
 * - `customerId` + `studioId` with no (resolvable) booking: record a floating,
 *   on-file paid deposit valid 12 months (the "abyss" the Sweeper chases) - the
 *   agent took the money before a date exists.
 */
export function recordManualPayment(opts: {
  bookingId?: string;
  customerId?: string;
  studioId?: StudioId;
  amountAud: number;
  method: 'eftpos' | 'cash' | 'bank-transfer';
  /** Where the money settled (card/transfer/cash); rides onto the ledger entry. */
  channel?: DepositChannel;
  by?: string;
}): void {
  if (opts.amountAud <= 0) return;
  const brain = useBrainStore.getState();
  const at = nowIso();
  const note = MANUAL_METHOD_LABEL[opts.method];
  const entry: DepositLedgerEntry = {
    at,
    amountAud: opts.amountAud,
    kind: 'paid',
    by: opts.by,
    note,
    ...(opts.channel ? { channel: opts.channel } : {}),
  };
  const booking = opts.bookingId ? brain.bookings[opts.bookingId] : undefined;

  // No booking to attach to: a floating, on-file paid deposit that enters the
  // 12-month on-file cohort. Needs an owner (customer + studio) to be a record.
  if (!booking) {
    const { customerId, studioId } = opts;
    if (!customerId || !studioId) return;
    const id = `dep-manual-${Date.now()}`;
    const deposit: Deposit = {
      id,
      customerId,
      studioId,
      amountAud: opts.amountAud,
      amountPaid: opts.amountAud,
      feesAud: 0,
      netAud: opts.amountAud,
      status: 'paid',
      channelSent: 'walk-in',
      stripePaymentLinkId: 'manual',
      method: opts.method,
      // On file, no date: valid 12 months from when it landed.
      expiresAt: onFileExpiryIso(at),
      createdAt: at,
      paidAt: at,
      policy: 'seven-day-rule',
      source: 'agent-initiated-chat',
      ledger: [entry],
      ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
    };
    brain.upsertDeposit(deposit);
    brain.incStat('deposits');
    brain.appendActivity({
      id: `act-manualpay-onfile-${id}`,
      type: 'deposit-paid',
      customerId,
      studioId,
      amount: opts.amountAud,
      body: `Manual payment ${formatAud(opts.amountAud)} (${note}) taken on file for ${customerId}. Awaiting a date.`,
      timestamp: at,
    });
    emitDepositReceipt(deposit, opts.amountAud, at);
    return;
  }

  const existing = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  let depositForReceipt: Deposit;

  if (!existing) {
    const id = `dep-manual-${Date.now()}`;
    const deposit: Deposit = {
      id,
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      amountAud: opts.amountAud,
      amountPaid: opts.amountAud,
      feesAud: 0,
      netAud: opts.amountAud,
      status: 'paid',
      channelSent: 'walk-in',
      stripePaymentLinkId: 'manual',
      method: opts.method,
      expiresAt: at,
      createdAt: at,
      paidAt: at,
      policy: 'seven-day-rule',
      source: booking.source,
      bookingId: booking.id,
      ledger: [entry],
      ...(currentAgentId() ? { takenByAgentId: currentAgentId() } : {}),
    };
    brain.upsertDeposit(deposit);
    brain.upsertBooking({ ...booking, depositId: id });
    brain.incStat('deposits');
    depositForReceipt = deposit;
  } else {
    const prevPaid = existing.amountPaid ?? (existing.status === 'paid' ? existing.amountAud : 0);
    const newPaid = prevPaid + opts.amountAud;
    const updated: Deposit = {
      ...existing,
      amountPaid: newPaid,
      status: newPaid >= existing.amountAud ? 'paid' : existing.status,
      method: opts.method,
      paidAt: existing.paidAt ?? at,
      ledger: [...(existing.ledger ?? []), entry],
    };
    brain.upsertDeposit(updated);
    depositForReceipt = updated;
  }
  brain.appendActivity({
    id: `act-manualpay-${booking.id}-${Date.now()}`,
    type: 'deposit-paid',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    amount: opts.amountAud,
    body: `Manual payment ${formatAud(opts.amountAud)} (${note}) recorded on ${booking.customerId}'s booking.`,
    timestamp: at,
  });
  emitDepositReceipt(depositForReceipt, opts.amountAud, at);
}

/**
 * Expire pending deposits whose window has elapsed: mark the deposit `expired`,
 * release the held placeholder booking (status -> cancelled, freeing the live
 * calendar slot) and retain both records so an expired link stays findable and
 * re-sendable. Never sets `bookingId` (the hold never locked). Mirrors
 * reconcileSequenceClock; "now" is the sim clock.
 */
export function reconcileDepositHolds(): void {
  const brain = useBrainStore.getState();
  const nowMs = new Date(nowIso()).getTime();
  for (const deposit of Object.values(brain.deposits)) {
    if (deposit.status !== 'pending') continue;
    if (new Date(deposit.expiresAt).getTime() > nowMs) continue;
    brain.upsertDeposit({ ...deposit, status: 'expired' });
    if (deposit.heldBookingId) {
      const held = brain.bookings[deposit.heldBookingId];
      if (held && held.status === 'held') {
        brain.upsertBooking({ ...held, status: 'cancelled' });
      }
    }
    brain.appendActivity({
      id: `act-dep-expired-${deposit.id}`,
      type: 'deposit-expired',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: deposit.artistId,
      amount: deposit.amountAud,
      body: `Deposit link expired for ${deposit.customerId}. Held slot released; record kept.`,
      timestamp: nowIso(),
    });
  }
}

function sessionLengthForAmount(amountAud: number): Booking['sessionLength'] {
  if (amountAud >= 500) return 'full-day';
  if (amountAud >= 300) return 'half-day';
  return 'small';
}

function castLinkChannel(c: string): 'sms' | 'meta-dm' | 'ig-dm' | 'email' {
  if (c === 'sms' || c === 'meta-dm' || c === 'ig-dm' || c === 'email') return c;
  return 'sms';
}

/** A deposit is effectively expired once its window has elapsed, even if a
 * reconcile pass has not yet flipped the persisted `status` field. */
export function isEffectivelyExpired(deposit: Deposit): boolean {
  if (deposit.status === 'expired') return true;
  return (
    deposit.status === 'pending' &&
    new Date(deposit.expiresAt).getTime() < new Date(nowIso()).getTime()
  );
}

/**
 * Resend / follow up on a deposit link. Two shapes are supported so both the
 * A+B (office / queue / ledger) and C (self-book follow-up) flows are preserved:
 *
 * - Object form `{ depositId, discussedSlot?, channel? }`: for an effectively
 *   expired record, mint a FRESH link + hold (reusing the old slot unless a new
 *   one is supplied), stamp the old record `supersededByDepositId` (so it reads
 *   "link resent") and `resentAt`, and return the fresh Deposit. The old record
 *   is retained and keeps its expired state. No-op (undefined) unless the source
 *   has effectively expired.
 *
 * - String form `resendDepositLink(depositId)`: non-destructive follow-up that
 *   flips the SAME record to a fresh 48h window and stamps `resentAt` so it
 *   surfaces as "link resent" in the ledger and the artist status list. Never
 *   deletes; never touches a paid record.
 */
export function resendDepositLink(
  arg:
    | string
    | {
        depositId: string;
        discussedSlot?: { artistId: string; startIso: string; endIso: string };
        channel?: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
      },
): Deposit | undefined {
  const brain = useBrainStore.getState();

  // String form (C8): flip the same record to a fresh 48h window in place.
  if (typeof arg === 'string') {
    const deposit = brain.deposits[arg];
    if (!deposit) return undefined;
    if (deposit.status === 'paid') return undefined;
    const now = nowIso();
    const expiresAt = new Date(new Date(now).getTime() + 48 * 60 * 60 * 1000).toISOString();
    const updated: Deposit = { ...deposit, status: 'pending', resentAt: now, expiresAt };
    brain.upsertDeposit(updated);
    brain.appendActivity({
      id: `act-dep-resent-${deposit.id}-${Date.now()}`,
      type: 'deposit-link-sent',
      customerId: deposit.customerId,
      studioId: deposit.studioId,
      artistId: deposit.artistId,
      body: `Deposit link ${deposit.depositReference ?? deposit.id} resent. Fresh 48h window.`,
      timestamp: now,
    });
    return updated;
  }

  // Object form (A+B): mint a fresh link + hold for an expired record and mark
  // the old one superseded (retained, still expired).
  const old = brain.deposits[arg.depositId];
  if (!old || !isEffectivelyExpired(old)) return undefined;
  const slot = arg.discussedSlot ?? old.discussedSlot;
  const fresh = generateDepositLink({
    customerId: old.customerId,
    studioId: old.studioId,
    artistId: old.artistId ?? 'pat',
    amountAud: old.amountAud,
    channel: arg.channel ?? castLinkChannel(old.channelSent),
    sessionLength: sessionLengthForAmount(old.amountAud),
    source: old.source as Booking['source'],
    ...(slot ? { discussedSlot: slot } : {}),
    ...(old.pieceName ? { pieceName: old.pieceName } : {}),
  });
  brain.upsertDeposit({ ...old, supersededByDepositId: fresh.id, resentAt: nowIso() });
  return fresh;
}

export function generateSelfBookLink(
  input: Omit<SelfBookLink, 'token' | 'generatedAt' | 'status' | 'depositReference'> & {
    depositReference?: string;
  },
): SelfBookLink {
  const token = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
  const link: SelfBookLink = {
    ...input,
    token,
    status: 'active',
    generatedAt: nowIso(),
    // Staff-sent links get a generic minted code; universal links pass their own
    // artist-derived reference (AL-ASHER-4F7K) via selfBookFlow.
    depositReference: input.depositReference ?? `REF-${token.slice(0, 6).toUpperCase()}`,
  };
  useBrainStore.getState().upsertSelfBookLink(link);
  useBrainStore.getState().appendActivity({
    id: `act-link-${token}`,
    type: 'self-book-link-sent',
    customerId: input.customerId,
    studioId: input.studioId,
    artistId: input.artistId,
    body: `Self-book link sent to ${input.customerId} via ${input.channelSent}. Expires ${input.expiresAt.slice(0, 16)}.`,
    timestamp: nowIso(),
  });
  return link;
}

/**
 * Forfeit a paid deposit whose booking was a no-show (G2, 14 Jul + 30 Jun 8h
 * rule). Writes a `forfeit` movement that zeroes the paid balance; the deposit
 * stays linked to the booking so it reads as `lapsed` in the ledger. Idempotent.
 * // ASSUMPTION: forfeit fires on no-show mark, uniform across shops, reversible.
 */
export function forfeitDepositForNoShow(
  bookingId: string,
  opts?: { by?: string; at?: string; note?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking?.depositId) return undefined;
  const deposit = brain.deposits[booking.depositId];
  if (!deposit) return undefined;
  const paid = depositPaidAud(deposit);
  if (paid <= 0) return deposit; // nothing to forfeit / already forfeited
  const at = opts?.at ?? nowIso();
  // Preserve the paid history (backfill one entry when the deposit has none) so
  // the forfeit nets to zero rather than going negative.
  const baseLedger: DepositLedgerEntry[] =
    deposit.ledger && deposit.ledger.length > 0
      ? deposit.ledger
      : [
          {
            at: deposit.paidAt ?? at,
            amountAud: paid,
            kind: 'paid',
            by: deposit.takenByAgentId ?? 'system',
          },
        ];
  const updated: Deposit = {
    ...deposit,
    ledger: [
      ...baseLedger,
      {
        at,
        amountAud: paid,
        kind: 'forfeit',
        fromBookingId: bookingId,
        by: opts?.by ?? 'system',
        // L1 (17 Jul): the floor forfeit forces a typed reason; carry it onto the
        // ledger note so the deposit book surfaces why. Falls back to the fixed 8h
        // string for the automatic (rule-driven) forfeit path.
        note: opts?.note?.trim() || 'No-show forfeiture (8h rule)',
      },
    ],
  };
  brain.upsertDeposit(updated);
  return updated;
}

/**
 * Forfeit a paid deposit on an Ops CANCEL made inside the 7-day window (tv-bli).
 * Mirrors `forfeitDepositForNoShow`'s ledger write (a `forfeit` movement that
 * zeroes the paid balance, keeping the deposit linked so it reads as lapsed) but
 * carries a cancel-appropriate reason and, crucially, does NOT flip the booking
 * to `no-show`. The caller sets the booking to `cancelled`. Idempotent.
 */
export function forfeitDeposit(
  bookingId: string,
  opts?: { reason?: string; by?: string; at?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking?.depositId) return undefined;
  const deposit = brain.deposits[booking.depositId];
  if (!deposit) return undefined;
  const paid = depositPaidAud(deposit);
  if (paid <= 0) return deposit; // nothing to forfeit / already forfeited
  const at = opts?.at ?? nowIso();
  // Preserve the paid history (backfill one entry when the deposit has none) so
  // the forfeit nets to zero rather than going negative.
  const baseLedger: DepositLedgerEntry[] =
    deposit.ledger && deposit.ledger.length > 0
      ? deposit.ledger
      : [
          {
            at: deposit.paidAt ?? at,
            amountAud: paid,
            kind: 'paid',
            by: deposit.takenByAgentId ?? 'system',
          },
        ];
  const updated: Deposit = {
    ...deposit,
    ledger: [
      ...baseLedger,
      {
        at,
        amountAud: paid,
        kind: 'forfeit',
        fromBookingId: bookingId,
        by: opts?.by ?? 'system',
        note: opts?.reason?.trim() || 'Cancelled inside 7-day window',
      },
    ],
  };
  brain.upsertDeposit(updated);
  return updated;
}

/**
 * Reinstate a forfeited (lapsed) deposit (G2, management-only — gate at the call
 * site with `canReinstateLapsedDeposit`). Writes a reversing `reinstate`
 * movement for the forfeited amount and unattaches the deposit from the
 * no-showed booking, so the existing deposit-on-file minting resurfaces it in
 * the money-cohort queue with no extra wiring.
 */
export function reinstateDeposit(
  depositId: string,
  by: string,
  opts?: { at?: string; note?: string },
): Deposit | undefined {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[depositId];
  if (!deposit) return undefined;
  const ledger = deposit.ledger ?? [];
  const forfeited = ledger.some((e) => e.kind === 'forfeit') && depositPaidAud(deposit) <= 0;
  // Reinstate only acts on a genuinely lapsed deposit: one that was forfeited, or
  // one still linked to a no-showed booking (lapsed before any auto-forfeit).
  const booking = deposit.bookingId ? brain.bookings[deposit.bookingId] : undefined;
  const onNoShow = booking?.status === 'no-show';
  if (!forfeited && !onNoShow) return deposit;
  const at = opts?.at ?? nowIso();
  // Reverse the forfeit (when there was one) for the forfeited amount; either way
  // unattach from the dead booking so deposit-on-file resurfaces it in the queue.
  const nextLedger = forfeited
    ? [
        ...ledger,
        {
          at,
          amountAud:
            [...ledger].reverse().find((e) => e.kind === 'forfeit')?.amountAud ?? deposit.amountAud,
          kind: 'reinstate' as const,
          by,
          note: opts?.note ?? 'Reinstated by management',
        },
      ]
    : ledger;
  const updated: Deposit = { ...deposit, status: 'paid', bookingId: undefined, ledger: nextLedger };
  brain.upsertDeposit(updated);
  return updated;
}
